import React from "react";
import { useTranslation } from "react-i18next";
import LabelForm from "pages/signup/form/labelForm";
import { poRejectedDurationTimeMin } from "utils/constantValues";

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between py-2 border-b border-default-color/30">
    <div className="text-primary font-semibold text-[14px]">{label}</div>
    <div className="text-base-black text-[14px]">{value}</div>
  </div>
);

const PoDetailsStep = ({ poDetails, onAccept, onReject, isLoading }) => {
  const [t] = useTranslation();
  if (!poDetails) return null;

  const {
    poIssuingDate,
    orderData = {},
    vendorData = {},
    vendorRate = {},
    sourceFiles = [],
    totalWithVat,
    vatTotal,
    vatRate,
    vendorRateTitle,
  } = poDetails;

  const currency = sourceFiles?.[0]?.currency || "";

  // Derive totals
  const netSubtotal = sourceFiles.reduce(
    (sum, f) => sum + (Number(f.subtotal) || 0),
    0
  );

  return (
    <div className="mt-[220px] px-[20%] xl-max:px-[10%] lg-max:px-[2rem] md-max:px-[1rem] flex flex-col mb-[97px] relative min-h-[calc(100vh-750px)]">
      <LabelForm
        label={t("poDetailsTitle")}
        labelClassName="text-primary font-bold text-[26px] md-max:text-[20px] not-italic mb-[20px] text-center"
      />

      <div className="max-w-[900px] w-full mx-auto p-4 rounded-[8px] border border-default-color/50 bg-white/80">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-primary font-semibold mb-2">
              {t("vendorInformation")}
            </div>
            <Row label={t("vendorName")} value={vendorData.vendorName || "-"} />
            <Row
              label={t("vendorAddress")}
              value={vendorData.vendorAddress || "-"}
            />
            <Row
              label={t("vendorPhoneNumber")}
              value={vendorData.vendorPhoneNumber || "-"}
            />
            <Row
              label={t("vendorEmailAddress")}
              value={vendorData.vendorEmailAddress || "-"}
            />
            <Row
              label={t("vendorTaxRegistrationNumber")}
              value={vendorData.vendorTaxRegisterationNumber || "-"}
            />
          </div>
          <div>
            <div className="text-primary font-semibold mb-2">
              {t("orderInformation")}
            </div>
            <Row label={t("poIssuingDate")} value={poIssuingDate || "-"} />
            <Row
              label={t("customerName")}
              value={orderData.customerName || "-"}
            />
            <Row
              label={t("customerOrderNumber")}
              value={orderData.customerOrderNumber || "-"}
            />
            <Row
              label={t("tarjamaContact")}
              value={orderData.tarjamaContact || "-"}
            />
            <Row
              label={t("tarjamaContactPhoneNumber")}
              value={orderData.tarjamaContactPhoneNumber || "-"}
            />
          </div>
        </div>

        <div className="mt-4">
          {sourceFiles.length > 0 && (
            <div className="text-[13px] text-dark-gray mb-2">
              <span className="font-semibold">{t("fileName")}:</span>{" "}
              {sourceFiles[0]?.srcFileName || "-"}
              {sourceFiles[0]?.deadline ? (
                <span className="ml-3">
                  <span className="font-semibold">{t("deadline")}:</span>{" "}
                  {sourceFiles[0].deadline}
                </span>
              ) : null}
            </div>
          )}
          <div className="rounded-[6px] border border-default-color/40">
            <div className="grid grid-cols-5 gap-2 p-2 text-[13px] bg-[#F4F4F5] border-b border-default-color/30">
              <div className="col-span-2 font-semibold">
                {t("serviceDetails")}
              </div>
              <div className="font-semibold">
                {t("unitCount")} ({vendorRateTitle || vendorRate.title || ""})
              </div>
              <div className="font-semibold">
                {t("unitPrice")}
                {currency ? ` (${currency})` : ""}
              </div>
              <div className="font-semibold text-right">
                {t("netSubtotal")}
                {currency ? ` (${currency})` : ""}
              </div>
            </div>
            {sourceFiles.map((it, idx) => (
              <div
                key={idx}
                className="grid grid-cols-5 gap-2 p-2 text-[13px] border-b last:border-b-0 border-default-color/30"
              >
                <div className="col-span-2">
                  {vendorRate.serviceTitle || "-"}
                </div>
                <div>{it.unitCount}</div>
                <div>{it.unitPrice}</div>
                <div className="text-right">{it.subtotal}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-4 text-[13px]">
          <div className="flex justify-end">
            <div className="w-[320px]">
              <div className="flex justify-between py-1">
                <span className="font-semibold">
                  {t("netSubtotal")}
                  {currency ? ` (${currency})` : ""}
                </span>
                <span>{netSubtotal}</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="font-semibold">
                  {t("netTotal")}
                  {currency ? ` (${currency})` : ""}
                </span>
                <span>{totalWithVat}</span>
              </div>
            </div>
          </div>
        </div>

        <p className="w-full text-red-500 text-left mt-4">
          <span className="font-bold">Important:</span> If no action is taken within {poRejectedDurationTimeMin} minutes, this Purchase Order will be automatically rejected.
        </p>

        <div className="flex items-center justify-center gap-[10px] mt-[24px]">
          <button
            type="button"
            onClick={onReject}
            disabled={isLoading.reject}
            className="cursor-pointer flex py-[12px] px-[16px] gap-[8px] justify-center items-center rounded-[6px] border-[1px] border-solid border-default-color bg-white h-[44px] w-[150px]"
          >
            <div className="text-base-black font-bold text-[14px] not-italic">
              {t("reject")}
            </div>
          </button>
          <button
            type="button"
            onClick={onAccept}
            disabled={isLoading.accept}
            className="cursor-pointer flex py-[12px] px-[16px] gap-[8px] justify-center items-center rounded-[6px] border-[1px] border-solid border-primary bg-primary h-[44px] w-[150px]"
          >
            <div className="text-white font-bold text-[14px] not-italic">
              {t("accept")}
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PoDetailsStep;
